import { useCallback, useEffect, useState } from 'react';
import {
  archiveRule,
  createRule,
  listRules,
  previewRule,
  publishRule,
  restoreRule,
  ruleVersions,
  updateRule,
  type PreviewResult,
  type RuleDraft,
  type RuleView,
  type VersionsResult,
} from './api';
import { RuleForm } from './rule-form';
import type { LoyaltyTranslate } from './i18n';
import {
  Button,
  Field,
  Reveal,
  StatusBadge,
  Table,
  TableSkeleton,
  TextInput,
  type BadgeTone,
} from '../../shell/kit';
import { useConfirm } from '../../shell/confirm';
import { useToast } from '../../shell/toast';
import { formatMoney } from '../../shell/format';

const STATUS_TONE: Record<RuleView['status'], BadgeTone> = {
  draft: 'warn',
  active: 'ok',
  archived: 'neutral',
};

function isoDay(offsetDays: number): string {
  return new Date(Date.now() + offsetDays * 86400000).toISOString().slice(0, 10);
}

function describeAction(action: Record<string, unknown>): string {
  // Колонка печатала сырые тиыны: «+100000 тиын» вместо «+1 000 ₸».
  if (action['kind'] === 'fixed') return `+${formatMoney(Number(action['amountMinor']))}`;
  if (action['kind'] === 'percent') return `${Number(action['percentBp']) / 100} %`;
  if (action['kind'] === 'multiplier') return `×${Number(action['factorBp']) / 10000}`;
  if (action['kind'] === 'burn') {
    return `≤ ${Number(action['maxShareBp']) / 100} % · 1 = ${Number(action['rateBp']) / 10000} ₸`;
  }
  return '—';
}

export interface RulesTabProps {
  readonly t: LoyaltyTranslate;
  readonly onError: (message: string) => void;
}

export function RulesTab({ t, onError }: RulesTabProps) {
  const confirm = useConfirm();
  const toast = useToast();
  const [rules, setRules] = useState<readonly RuleView[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<RuleView | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<RuleView | undefined>(undefined);
  const [preview, setPreview] = useState<PreviewResult | undefined>(undefined);
  const [versions, setVersions] = useState<VersionsResult | undefined>(undefined);
  const [from, setFrom] = useState(isoDay(-30));
  const [to, setTo] = useState(isoDay(1));

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      setRules(await listRules());
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function guarded(run: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await run();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function submit(draft: RuleDraft): void {
    void guarded(async () => {
      if (editing) await updateRule(editing.id, draft);
      else await createRule(draft);
      setEditing(undefined);
      setCreating(false);
      await reload();
    });
  }

  function runPreview(rule: RuleView): void {
    void guarded(async () => {
      setSelected(rule);
      setPreview(
        await previewRule(rule.id, new Date(from).toISOString(), new Date(to).toISOString()),
      );
    });
  }

  function openVersions(rule: RuleView): void {
    void guarded(async () => {
      setSelected(rule);
      setVersions(await ruleVersions(rule.id));
    });
  }

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-ink">{t('rules.title')}</h3>
        <button
          type="button"
          className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
          onClick={() => {
            setEditing(undefined);
            setCreating(true);
          }}
        >
          {t('rules.create')}
        </button>
      </header>

      {loading ? <TableSkeleton rows={4} columns={7} label={t('common.loading')} /> : null}

      {(creating || editing) && (
        <RuleForm
          t={t}
          initial={editing}
          busy={busy}
          onSubmit={submit}
          onCancel={() => {
            setCreating(false);
            setEditing(undefined);
          }}
        />
      )}

      {rules.length === 0 ? (
        <p className="text-ink-muted">{t('rules.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded border border-line bg-surface">
          <Table className="text-left">
            <thead className="bg-sunken text-xs uppercase text-ink-muted">
              <tr>
                <th className="px-3 py-2">{t('rules.code')}</th>
                <th className="px-3 py-2">{t('rules.name')}</th>
                <th className="px-3 py-2">{t('rules.version')}</th>
                <th className="px-3 py-2">{t('rules.action')}</th>
                <th className="px-3 py-2">{t('rules.priority')}</th>
                <th className="px-3 py-2">{t('rules.status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} className="border-t border-line">
                  <td className="px-3 py-2 font-mono text-xs">{rule.code}</td>
                  <td className="px-3 py-2">{rule.name}</td>
                  <td className="px-3 py-2">{rule.version}</td>
                  <td className="px-3 py-2">{describeAction(rule.action)}</td>
                  <td className="px-3 py-2">{rule.priority}</td>
                  <td className="px-3 py-2">
                    <StatusBadge tone={STATUS_TONE[rule.status]}>
                      {t(`rules.status.${rule.status}`)}
                    </StatusBadge>
                  </td>
                  {/*
                    * Действия — кнопками с промежутком, а не подчёркнутым
                    * текстом впритык: четыре ссылки подряд сливались в одну
                    * полосу, и попасть в нужную с первого раза не выходило.
                    */}
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap justify-end gap-2">
                    <Button size="sm" onClick={() => runPreview(rule)}>
                      {t('rules.preview')}
                    </Button>
                    <Button size="sm" onClick={() => openVersions(rule)}>
                      {t('rules.versions')}
                    </Button>
                    {rule.status === 'draft' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => {
                            setCreating(false);
                            setEditing(rule);
                          }}
                        >
                          {t('rules.save')}
                        </Button>
                        <Button
                          size="sm"
                          tone="primary"
                          disabled={busy}
                          onClick={() =>
                            void (async () => {
                              // Публикация меняет правила начисления денег по всей
                              // сети. Раньше это был один клик без подтверждения и
                              // без ответа на вопрос «кого это затронет», хотя
                              // предпросмотр рядом умеет его посчитать.
                              const ok = await confirm({
                                title: t('rules.publishConfirm', { code: rule.code }),
                                tone: 'danger',
                                confirmLabel: t('rules.publish'),
                                consequences: [
                                  t('rules.publishEffect'),
                                  preview && selected?.id === rule.id
                                    ? t('rules.publishPreview', {
                                        orders: preview.ordersAffected,
                                        customers: preview.customersAffected,
                                        delta: formatMoney(preview.deltaMinor),
                                      })
                                    : t('rules.publishNoPreview'),
                                ],
                              });
                              if (!ok) return;
                              await guarded(async () => {
                                await publishRule(rule.id);
                                await reload();
                                toast.ok(t('rules.published', { code: rule.code }));
                              });
                            })()
                          }
                        >
                          {t('rules.publish')}
                        </Button>
                      </>
                    )}
                    {rule.status === 'active' && (
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          void guarded(async () => {
                            await archiveRule(rule.id);
                            await reload();
                          })
                        }
                      >
                        {t('rules.archive')}
                      </Button>
                    )}
                    {rule.status === 'archived' && (
                      <Button
                        size="sm"
                        tone="primary"
                        disabled={busy}
                        onClick={() =>
                          void (async () => {
                            /*
                             * Возврат — это откат к прежней версии, а не второе
                             * активное правило: действующая версия того же кода
                             * уйдёт в архив. Кто именно уйдёт, сказано прямо в
                             * подтверждении: иначе маркетолог узнаёт об этом уже
                             * по изменившимся начислениям.
                             */
                            const current = rules.find(
                              (r) => r.code === rule.code && r.status === 'active',
                            );
                            const ok = await confirm({
                              title: t('rules.restoreConfirm', {
                                code: rule.code,
                                version: rule.version,
                              }),
                              tone: 'danger',
                              confirmLabel: t('rules.restore'),
                              consequences: [
                                t('rules.restoreEffect'),
                                current
                                  ? t('rules.restoreSupersedes', { version: current.version })
                                  : t('rules.restoreNoActive'),
                              ],
                            });
                            if (!ok) return;
                            await guarded(async () => {
                              await restoreRule(rule.id);
                              await reload();
                              toast.ok(t('rules.restored', { code: rule.code }));
                            });
                          })()
                        }
                      >
                        {t('rules.restore')}
                      </Button>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/*
        * Предпросмотр и версии стоят под таблицей и раньше открывались молча:
        * нажатие на строке считало результат за пределами экрана. Теперь панель
        * подтягивает себя в вид.
        */}
      <Reveal when={selected?.id}>
      <div className="rounded-card border border-line bg-surface p-4">
        <h4 className="mb-1 font-medium text-ink">{t('rules.preview')}</h4>
        <p className="mb-3 text-xs text-ink-muted">{t('rules.preview.hint')}</p>
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('rules.preview.from')}>
            <TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={t('rules.preview.to')}>
            <TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <span className="text-xs text-ink-muted">
            {selected ? `${selected.code} v${selected.version}` : '—'}
          </span>
        </div>

        {preview && (
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs text-ink-muted">{t('rules.preview.scanned')}</dt>
              <dd className="font-medium">{preview.ordersScanned}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('rules.preview.orders')}</dt>
              <dd className="font-medium">{preview.ordersAffected}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('rules.preview.customers')}</dt>
              <dd className="font-medium">{preview.customersAffected}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('rules.preview.delta')}</dt>
              <dd className="font-medium tabular">{formatMoney(preview.deltaMinor)}</dd>
            </div>
            {preview.truncated && (
              <p className="col-span-full text-xs text-warn">{t('rules.preview.truncated')}</p>
            )}
          </dl>
        )}
      </div>
      </Reveal>

      {versions && (
        <Reveal when={versions.code}>
        <div className="rounded-card border border-line bg-surface p-4">
          <h4 className="mb-2 font-medium text-ink">
            {t('rules.versions')} · {versions.code}
          </h4>
          <ul className="mb-4 space-y-1 text-sm">
            {versions.versions.map((v) => (
              <li key={v.id} className="flex items-center gap-2">
                <span className="font-mono text-xs">v{v.version}</span>
                <StatusBadge tone={STATUS_TONE[v.status]}>
                  {t(`rules.status.${v.status}`)}
                </StatusBadge>
                <span className="text-ink-muted">{describeAction(v.action)}</span>
              </li>
            ))}
          </ul>
          <h4 className="mb-2 font-medium text-ink">{t('rules.history')}</h4>
          <ul className="space-y-1 text-xs text-ink-muted">
            {versions.history.map((row) => (
              <li key={row.id}>
                {new Date(row.createdAt).toLocaleString()} · {row.action}
              </li>
            ))}
          </ul>
        </div>
        </Reveal>
      )}
    </section>
  );
}
